import { useMemo, useState } from 'react';
import { ChevronsUpDown, Loader2, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import type { ContractFunction } from '@mls-wallet/shared-types';

/** A selected function with its name and 4-byte selector */
type SelectedFunction = {
  name: string;
  selector: string;
};

export type FunctionSelectorValue = {
  proxyFunctions: SelectedFunction[];
  implementationFunctions: SelectedFunction[];
};

interface FunctionSelectorProps {
  value: FunctionSelectorValue;
  onValueChange: (value: FunctionSelectorValue) => void;
  proxyFunctions: ContractFunction[];
  implementationFunctions: ContractFunction[];
  isLoading?: boolean;
  error?: unknown;
  className?: string;
  /**
   * When provided, the selector stays clickable, but "specific function" selection is blocked.
   * (Used for unverified contracts.)
   */
  specificModeBlockedMessage?: string | null;
}

function toUniqueSortedNames(functions: ContractFunction[]): string[] {
  const set = new Set<string>();
  for (const f of functions) set.add(f.name);
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Get selector for a function name from the available functions */
function getSelector(
  name: string,
  functions: ContractFunction[]
): string | undefined {
  return functions.find((f) => f.name === name)?.selector;
}

type FunctionSelectorMode = 'any' | 'specific';

export function FunctionSelector({
  value,
  onValueChange,
  proxyFunctions,
  implementationFunctions,
  isLoading = false,
  error,
  className,
  specificModeBlockedMessage = null,
}: FunctionSelectorProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [tempValue, setTempValue] = useState<FunctionSelectorValue>(value);
  const [tempMode, setTempMode] = useState<FunctionSelectorMode>(() => {
    const totalCount =
      value.proxyFunctions.length + value.implementationFunctions.length;
    return totalCount > 0 ? 'specific' : 'any';
  });
  const [searchQuery, setSearchQuery] = useState('');

  const selectedNames = useMemo(() => {
    return new Set<string>([
      ...(value.proxyFunctions?.map((f) => f.name) ?? []),
      ...(value.implementationFunctions?.map((f) => f.name) ?? []),
    ]);
  }, [value.implementationFunctions, value.proxyFunctions]);

  const totalSelected = selectedNames.size;
  const isAny = totalSelected === 0;

  const displayValue = useMemo(() => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span>Loading functions…</span>
        </div>
      );
    }

    if (isAny) return 'Any function';
    return `Selected ${totalSelected} function${totalSelected === 1 ? '' : 's'}`;
  }, [isAny, isLoading, totalSelected]);

  const availableProxyNames = useMemo(() => {
    return new Set<string>(toUniqueSortedNames(proxyFunctions));
  }, [proxyFunctions]);

  const availableImplementationNames = useMemo(() => {
    return new Set<string>(toUniqueSortedNames(implementationFunctions));
  }, [implementationFunctions]);

  const allNames = useMemo(() => {
    // Old UX: proxy functions listed first, then implementation functions.
    // We still dedupe by name; if a name exists in both, it appears once in the proxy section.
    const proxyFirst = Array.from(availableProxyNames).sort((a, b) =>
      a.localeCompare(b)
    );
    const implOnly = Array.from(availableImplementationNames)
      .filter((name) => !availableProxyNames.has(name))
      .sort((a, b) => a.localeCompare(b));
    return [...proxyFirst, ...implOnly];
  }, [availableImplementationNames, availableProxyNames]);

  const filteredNames = useMemo(() => {
    if (!searchQuery) return allNames;
    const q = searchQuery.toLowerCase();
    return allNames.filter((name) => name.toLowerCase().includes(q));
  }, [allNames, searchQuery]);

  const handleOpen = () => {
    setTempValue(value);
    const totalCount =
      (value.proxyFunctions?.length ?? 0) +
      (value.implementationFunctions?.length ?? 0);
    setTempMode(totalCount > 0 ? 'specific' : 'any');
    setSearchQuery('');
    setOpen(true);
  };

  const handleCancel = () => {
    setTempValue(value);
    const totalCount =
      (value.proxyFunctions?.length ?? 0) +
      (value.implementationFunctions?.length ?? 0);
    setTempMode(totalCount > 0 ? 'specific' : 'any');
    setSearchQuery('');
    setOpen(false);
  };

  const handleSave = () => {
    onValueChange(tempValue);
    setOpen(false);
  };

  const toggleName = (name: string) => {
    if (specificModeBlockedMessage) return;
    setTempValue((prev) => {
      const isSelected =
        prev.proxyFunctions.some((f) => f.name === name) ||
        prev.implementationFunctions.some((f) => f.name === name);

      let nextProxy = prev.proxyFunctions;
      if (availableProxyNames.has(name)) {
        if (isSelected) {
          nextProxy = prev.proxyFunctions.filter((f) => f.name !== name);
        } else {
          const selector = getSelector(name, proxyFunctions);
          if (selector) {
            nextProxy = [...prev.proxyFunctions, { name, selector }];
          }
        }
      }

      let nextImplementation = prev.implementationFunctions;
      if (availableImplementationNames.has(name)) {
        if (isSelected) {
          nextImplementation = prev.implementationFunctions.filter(
            (f) => f.name !== name
          );
        } else {
          const selector = getSelector(name, implementationFunctions);
          if (selector) {
            nextImplementation = [
              ...prev.implementationFunctions,
              { name, selector },
            ];
          }
        }
      }

      return {
        proxyFunctions: nextProxy,
        implementationFunctions: nextImplementation,
      };
    });
  };

  const isTempAny =
    tempValue.proxyFunctions.length === 0 &&
    tempValue.implementationFunctions.length === 0;

  return (
    <>
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        onClick={handleOpen}
        size="lg"
        className={cn('w-full justify-between', className)}
      >
        {displayValue}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Functions</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {specificModeBlockedMessage ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {specificModeBlockedMessage}
              </div>
            ) : null}

            {error ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                Failed to load functions.
              </div>
            ) : null}

            <RadioGroup
              value={tempMode}
              onValueChange={(nextMode) => {
                if (nextMode === 'any') {
                  setTempMode('any');
                  setTempValue({
                    proxyFunctions: [],
                    implementationFunctions: [],
                  });
                  setSearchQuery('');
                } else {
                  setTempMode('specific');
                }
              }}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="any" id="any-function" />
                <Label htmlFor="any-function">Any function</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="specific" id="specific-function" />
                <Label htmlFor="specific-function">Specific functions</Label>
              </div>
            </RadioGroup>

            {tempMode === 'specific' ? (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search functions..."
                    className="pl-9"
                  />
                </div>

                <ScrollArea className="h-[320px] rounded-md border p-2">
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading…
                    </div>
                  ) : filteredNames.length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">
                      No functions found.
                    </div>
                  ) : (
                    <div className="space-y-0">
                      {filteredNames.map((name, idx) => {
                        const checked =
                          tempValue.proxyFunctions.some(
                            (f) => f.name === name
                          ) ||
                          tempValue.implementationFunctions.some(
                            (f) => f.name === name
                          );

                        const id = `fn:${name}`;
                        const isProxyOnly =
                          availableProxyNames.has(name) &&
                          !availableImplementationNames.has(name);

                        return (
                          <div key={id}>
                            <button
                              type="button"
                              onClick={() => toggleName(name)}
                              disabled={Boolean(specificModeBlockedMessage)}
                              className={cn(
                                'w-full text-left p-3 flex items-center justify-between transition-colors',
                                specificModeBlockedMessage
                                  ? 'opacity-60 cursor-not-allowed'
                                  : checked
                                    ? 'bg-accent hover:bg-accent'
                                    : 'bg-background hover:bg-accent'
                              )}
                            >
                              <span className="text-sm font-medium">
                                {name}{' '}
                                {isProxyOnly ? (
                                  <span className="text-muted-foreground">
                                    (proxy)
                                  </span>
                                ) : null}
                              </span>
                              {checked ? (
                                <span className="w-2 h-2 bg-primary rounded-full ml-2" />
                              ) : null}
                            </button>
                            {idx < filteredNames.length - 1 ? (
                              <Separator className="w-[calc(100%-16px)] mx-auto" />
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>

                {isTempAny ? null : (
                  <div className="p-3 bg-accent rounded-md">
                    <div className="text-sm font-medium mb-2">
                      Selected (
                      {tempValue.proxyFunctions.length +
                        tempValue.implementationFunctions.length}
                      ):
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {[
                        ...tempValue.proxyFunctions.map((f) => f.name),
                        ...tempValue.implementationFunctions.map((f) => f.name),
                      ].join(', ')}
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
